import { platformById, type SampleDeal } from './data';
import { CRITICAL_MS, URGENT_MS, deco, isLive, type Urgency } from './deco';
import { cd, cdShort, fD, fDY, hm, money, pct, short, spoken } from './format';
import { FIRST_PACE, MOMENTUM, MOMENTUM_FROM } from './momentum';

/**
 * "What an alert looks like": the six alert emails, the first five drawn
 * from the sample deals at one instant. Pure, so the server render
 * (view.ts), the ticking browser (client/alerts.ts) and the tests agree.
 * Copy is verbatim from the "Alerts" brief for RaiseSonar Public.dc.html,
 * except where design/README.md says otherwise (9 Oct 2026). Momentum came
 * with the "Momentum Marketing" brief (28 Oct 2026); its three emails are
 * the Momentum section's raises (momentum.ts), not the sample deals.
 *
 * Subjects are what each email said when it was sent, so they are drawn
 * once. The countdowns and provenance inside the emails tick.
 */

export type AlertTypeId =
  'new' | 'closing' | 'cap' | 'perk' | 'weekly' | 'momentum';

export interface AlertType {
  id: AlertTypeId;
  name: string;
  glyph: string;
  when: string;
  description: string;
  /** The envelope: who the sample email is from, and when it was sent. */
  from: string;
  sent: string;
  /** A "NEW" tag after the name. */
  isNew?: boolean;
}

export const ALERT_TYPES: readonly AlertType[] = [
  {
    id: 'new',
    name: 'New raise',
    glyph: '○',
    when: 'Instant',
    description: 'The moment a raise that matches one of your rules goes live.',
    from: 'Raise Sonar Alerts',
    sent: 'Thu 24 Sep, 16:20',
  },
  {
    id: 'closing',
    name: 'Closing soon',
    glyph: '▲',
    when: '7d · 72h · 24h',
    description: 'Raises you follow, grouped, as each reminder window opens.',
    from: 'Raise Sonar Alerts',
    sent: 'Fri 25 Sep, 10:00',
  },
  {
    id: 'cap',
    name: 'Near cap',
    glyph: '◆',
    when: '≥ 90% of max',
    description:
      'A raise you follow is nearly full and could close before its deadline.',
    from: 'Raise Sonar Alerts',
    sent: 'Thu 24 Sep, 07:00',
  },
  {
    id: 'perk',
    name: 'Perk expiring',
    glyph: '■',
    when: 'Before it ends',
    description:
      'An early-bird bonus or discount on a raise you follow is about to end.',
    from: 'Raise Sonar Alerts',
    sent: 'Fri 25 Sep, 08:00',
  },
  {
    id: 'weekly',
    name: 'Weekly Sonar',
    glyph: '≡',
    when: 'Mon 08:00 · free',
    description:
      'New, closing and near-cap raises from the week, plus platform news.',
    from: 'Raise Sonar Weekly',
    sent: 'Mon 28 Sep, 08:00',
  },
  {
    id: 'momentum',
    name: 'Momentum',
    glyph: '↗',
    when: 'When pace changes',
    description:
      'A raise you follow speeds up to 2× its usual pace, comes within reach of its goal, or slows down.',
    from: MOMENTUM_FROM,
    sent: MOMENTUM[FIRST_PACE].email.time,
    isNew: true,
  },
];

const WORDS = ['none', 'one', 'two', 'three', 'four', 'five', 'six', 'seven'];

/** "3 of 6 seen", then the line that sends them to choose. */
export function seenLabel(seen: number, total: number): string {
  return seen >= total
    ? `✓ Seen all ${WORDS[total] ?? total}. Now pick yours.`
    : `${seen} of ${total} seen`;
}

const DISCLAIMER = 'Manage alerts · Unsubscribe. Not investment advice.';

function byId(deals: readonly SampleDeal[], id: string): SampleDeal {
  const d = deals.find((x) => x.id === id);
  if (!d) throw new Error(`Unknown deal ${id}`);
  return d;
}

const platformOf = (deal: SampleDeal) => platformById(deal.platform).name;

/** "SAFE" from "SAFE · $18M cap": the security, without its terms. */
function securityOf(terms: string): string {
  return terms.split(' · ')[0];
}

/** "SAFE, $18M cap": the security, then its terms where it states any. */
function securityAndTerms(terms: string): string {
  const [security, ...rest] = terms.split(' · ');
  return [security, rest.join(' · ')].filter(Boolean).join(', ');
}

/** Raised as a share of the maximum, or null without both. */
function shareOfMax(deal: SampleDeal): number | null {
  return deal.raised !== null && deal.max
    ? (deal.raised / deal.max) * 100
    : null;
}

/* ---- Closing countdowns ---------------------------------------------------- */

/** "■ 2d 7h" in parts: the urgency glyph, the coarse countdown, its tone. */
export function closesBadge(
  deal: SampleDeal,
  now: number,
): { glyph: string; left: string; urgency: Urgency } {
  const left = deal.closesAt - now;
  const d = deco(deal, now);
  return {
    glyph: d.urgencyGlyph,
    left: left > 0 ? cdShort(left) : 'Closed',
    urgency: d.urgency,
  };
}

/** "↳ Companisto · checked 4 min ago". */
export function alertProvenance(deal: SampleDeal, now: number): string {
  return `↳ ${deco(deal, now).provenance}`;
}

/* ---- 1 · New raise ------------------------------------------------------------- */

const MISO_CLOSE_ZONE = 'America/New_York';

/** The New raise email's provenance, which also names the offering's filing. */
export function newRaiseProvenance(deal: SampleDeal, now: number): string {
  return `${alertProvenance(deal, now)} · SEC Form 1-A qualified 10 Sep 2026`;
}

/** Miso Robotics, as the section showed it before there were five types. */
export function newRaiseEmail(deal: SampleDeal, now: number) {
  const d = deco(deal, now);
  return {
    id: deal.id,
    subject: `New raise: ${deal.name} on ${platformOf(deal)} · ${deal.regime}`,
    name: deal.name,
    raised: `${money(deal.raised ?? 0, deal.currency)} · ${deal.investors} investors`,
    minimum: money(deal.minTicket, deal.currency),
    regime: deal.regime,
    security: deal.terms,
    close: `Closes ${fDY(deal.closesAt, MISO_CLOSE_ZONE)} · ${hm(deal.closesAt, MISO_CLOSE_ZONE)} ET`,
    countdown: cd(deal.closesAt - now),
    spoken: spoken(deal.closesAt - now),
    glyph: d.urgencyGlyph,
    urgency: d.urgency,
    platform: platformOf(deal),
    provenance: newRaiseProvenance(deal, now),
  };
}

/* ---- 2 · Closing soon ------------------------------------------------------- */

/** Live raises that close within 7 days, soonest first. */
export function closingSoon(
  deals: readonly SampleDeal[],
  now: number,
): SampleDeal[] {
  return deals
    .filter((d) => isLive(d, now) && d.closesAt - now <= URGENT_MS)
    .sort((a, b) => a.closesAt - b.closesAt);
}

export function closingEmail(deals: readonly SampleDeal[], now: number) {
  const list = closingSoon(deals, now);
  const inside72 = list.filter((d) => d.closesAt - now <= CRITICAL_MS).length;
  const n = list.length;
  return {
    subject: `${n} ${n === 1 ? 'raise you follow closes' : 'raises you follow close'} within 7 days · ${inside72} inside 72 hours`,
    rows: list.slice(0, 5).map((deal) => {
      const raised =
        deal.raised === null ? [] : [money(deal.raised, deal.currency)];
      return {
        id: deal.id,
        name: deal.name,
        line:
          [platformOf(deal), deal.regime, ...raised].join(' · ') +
          (deco(deal, now).nearCap ? ' · ◆ near cap' : ''),
        ...closesBadge(deal, now),
      };
    }),
  };
}

/* ---- 3 · Near cap ---------------------------------------------------------- */

/** Elona Health while it is near its cap, else the fullest raise that is. */
export function nearCapDeal(
  deals: readonly SampleDeal[],
  now: number,
): SampleDeal {
  const near = deals
    .filter((d) => isLive(d, now) && deco(d, now).nearCap)
    .sort((a, b) => (shareOfMax(b) ?? 0) - (shareOfMax(a) ?? 0));
  return near.find((d) => d.id === 'elona') ?? near[0] ?? byId(deals, 'elona');
}

/** "Target €100,000 met", as the real near-cap email says it. */
function targetNote(deal: SampleDeal): string | null {
  if (deal.target === null) return null;
  const target = money(deal.target, deal.currency);
  return (deal.raised ?? 0) >= deal.target
    ? `Target ${target} met`
    : `Target ${target} not met yet`;
}

export function nearCapEmail(deal: SampleDeal, now: number) {
  const d = deco(deal, now);
  const raised = deal.raised ?? 0;
  const max = deal.max ?? 0;
  const share = pct(shareOfMax(deal) ?? 0);
  const note = targetNote(deal);
  return {
    id: deal.id,
    subject: `${deal.name} is at ${share} of its cap · ${money(Math.max(0, max - raised), deal.currency)} left`,
    kicker: `◆ NEAR CAP · ${share} OF MAXIMUM`,
    name: deal.name,
    line: [
      platformOf(deal),
      deal.regime,
      deal.country,
      deal.sector,
      securityOf(deal.terms),
    ].join(' · '),
    raised: money(raised, deal.currency),
    ofMax: `${share} of ${money(max, deal.currency)}`,
    fill: d.fill.toFixed(2),
    targetAt: d.targetAt === null ? null : d.targetAt.toFixed(2),
    note: `${note ? `${note}. ` : ''}Raises at their cap can close before the deadline.`,
    closes: `Closes ${fD(deal.closesAt)}`,
    ...closesBadge(deal, now),
    platform: platformOf(deal),
    provenance: alertProvenance(deal, now),
    why: `Why you got this: ${deal.name} is on your watchlist and you asked for near-cap alerts. ${DISCLAIMER}`,
  };
}

/* ---- 4 · Perk expiring ----------------------------------------------------- */

type Perk = NonNullable<SampleDeal['perk']>;

function perkOf(deal: SampleDeal): Perk {
  if (!deal.perk) throw new Error(`${deal.id} has no perk`);
  return deal.perk;
}

/** The live raise whose perk ends soonest, else Tessel Labs. */
export function perkDeal(
  deals: readonly SampleDeal[],
  now: number,
): SampleDeal {
  const running = deals
    .filter((d) => isLive(d, now) && d.perk && d.perk.endsAt > now)
    .sort((a, b) => perkOf(a).endsAt - perkOf(b).endsAt);
  return running[0] ?? byId(deals, 'tessel');
}

/** "1d 18:59:00 left", ticking; "Ended" once it has. */
export function perkLeft(deal: SampleDeal, now: number): string {
  const left = perkOf(deal).endsAt - now;
  return left > 0 ? `${cd(left)} left` : 'Ended';
}

/** What a screen reader hears for perkLeft(): "Ends in 1 day, 18 hours". */
export function perkSpoken(deal: SampleDeal, now: number): string {
  return spoken(perkOf(deal).endsAt - now, 'Ends');
}

export function perkEmail(deal: SampleDeal, now: number) {
  const perk = perkOf(deal);
  const left = perk.endsAt - now;
  const what = `${deal.name} ${perk.text.toLowerCase()}`;
  const share = shareOfMax(deal);
  return {
    id: deal.id,
    subject:
      left > 0 ? `${what} ends in ${cdShort(left)}` : `${what} has ended`,
    name: deal.name,
    line: [
      platformOf(deal),
      deal.regime,
      deal.region,
      deal.sector,
      securityAndTerms(deal.terms),
    ].join(' · '),
    perk: perk.text.toUpperCase(),
    left: perkLeft(deal, now),
    spoken: perkSpoken(deal, now),
    invest: `Investments on ${platformOf(deal)} before then qualify.`,
    closes: `Raise closes ${fD(deal.closesAt)}`,
    figures: [
      deal.raised === null ? null : money(deal.raised, deal.currency),
      share === null ? null : `${pct(share)} of max`,
    ]
      .filter(Boolean)
      .join(' · '),
    platform: platformOf(deal),
    provenance: alertProvenance(deal, now),
    why: `Why you got this: ${deal.name} is on your watchlist and you asked for perk reminders. ${DISCLAIMER}`,
  };
}

/* ---- 5 · Weekly Sonar ------------------------------------------------------ */

/**
 * The week as the free digest sums it up: what is live, what launched in
 * the last 7 days (newest first), what closes within 7 days and what is near
 * its cap (fullest first).
 */
export function weeklyDigest(deals: readonly SampleDeal[], now: number) {
  const live = deals.filter((d) => isLive(d, now));
  return {
    live,
    fresh: live
      .filter((d) => now - d.launchAt <= URGENT_MS)
      .sort((a, b) => b.launchAt - a.launchAt),
    closing: closingSoon(deals, now),
    nearCap: live
      .filter((d) => deco(d, now).nearCap)
      .sort((a, b) => (shareOfMax(b) ?? 0) - (shareOfMax(a) ?? 0)),
  };
}

/**
 * `sources` is what the site's facts say it reads, "SEC EDGAR + 26
 * platforms"; null leaves the line out.
 */
export function weeklyEmail(
  deals: readonly SampleDeal[],
  now: number,
  sources: string | null,
) {
  const w = weeklyDigest(deals, now);
  return {
    subject: `Weekly Sonar: ${w.fresh.length} new, ${w.closing.length} closing this week, ${w.nearCap.length} near cap`,
    stats: [
      { n: w.live.length, label: 'live', tone: 'live' },
      { n: w.fresh.length, label: 'new', tone: 'new' },
      { n: w.closing.length, label: 'closing', tone: 'closing' },
      { n: w.nearCap.length, label: 'near cap', tone: 'cap' },
    ],
    fresh: w.fresh.slice(0, 4).map((deal) => ({
      region: deal.region,
      name: deal.name,
      line: [
        platformOf(deal),
        deal.sector,
        ...(deal.raised === null
          ? []
          : [`${money(deal.raised, deal.currency)} raised`]),
      ].join(' · '),
    })),
    closing: w.closing.slice(0, 4).map((deal) => ({
      id: deal.id,
      line: `${deal.name} · ${platformOf(deal)}`,
      ...closesBadge(deal, now),
    })),
    nearCap: w.nearCap.slice(0, 3).map((deal) => ({
      pct: `◆ ${pct(shareOfMax(deal) ?? 0)}`,
      line: `${deal.name} · ${money(deal.raised ?? 0, deal.currency)} of ${short(deal.max ?? 0, deal.currency)}`,
    })),
    source: sources === null ? null : `↳ Sources: ${sources}`,
  };
}
